import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from '../../../config/dayjs';
import type { TicketMessage } from '../../../types/support';

const ROLE_ICON: Record<TicketMessage['role'], string> = {
  user: 'person',
  assistant: 'smart_toy',
  agent: 'support_agent',
  system: 'info',
};

/**
 * One turn of the conversation, as a chat bubble: the user on the left, the
 * agent and the bot on the right, system notes centred. Tool calls and
 * retrieved refs collapse behind a toggle: an agent needs to see *why* the
 * bot answered as it did, but not on every line while scanning the chat.
 */
export function TranscriptMessage({ message }: { message: TicketMessage }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Citation refs, e.g. `ev-backend/src/session/session.service.ts:120-180`.
  // These used to be chunk ids, which said nothing to an agent and dangled the
  // moment the knowledge base was re-ingested.
  const retrievedRefs = message.retrievedRefs ?? [];
  const hasDebug = (message.toolCalls?.length ?? 0) > 0 || retrievedRefs.length > 0;

  return (
    <div className={`chat-row is-${message.role}`}>
      {message.role !== 'system' && (
        <span className="chat-author">
          <span className="material-symbols-outlined" aria-hidden="true">
            {ROLE_ICON[message.role]}
          </span>
          {t(`dashboard.support.role.${message.role}`)}
          {hasDebug && (
            <button
              type="button"
              className="chat-debug-toggle"
              onClick={() => setOpen((value) => !value)}
              aria-label={t('dashboard.support.toggleDetails')}
              aria-expanded={open}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                expand_more
              </span>
            </button>
          )}
        </span>
      )}

      <div className="chat-bubble">{message.content}</div>

      {/* Kept out of the DOM (not merely hidden) until the agent asks for
          it, so collapsed content is not reachable. */}
      {hasDebug && open && (
        <div className="chat-debug">
          {message.toolCalls?.map((call, index) => (
            <div key={`${call.name}-${index}`}>
              {call.ok ? '✓' : '✗'} {call.name}({JSON.stringify(call.args)}){' '}
              {call.ms}ms {call.error ?? ''}
            </div>
          ))}
          {retrievedRefs.length > 0 && (
            <div>
              <div>
                {t('dashboard.support.retrievedChunks', { count: retrievedRefs.length })}
              </div>
              {retrievedRefs.map((ref) => (
                <div key={ref}>{ref}</div>
              ))}
            </div>
          )}
          {message.usage && (
            <div>
              {t('dashboard.support.tokenUsage', {
                prompt: message.usage.promptTokens,
                completion: message.usage.completionTokens,
              })}
            </div>
          )}
        </div>
      )}

      <time className="chat-time" dateTime={message.createdAt}>
        {dayjs(message.createdAt).format('DD MMM HH:mm')}
      </time>
    </div>
  );
}
